import GenericButton from "@ui-components/Button/GenericButton";
import React from "react";
import { ReactComponent as DownloadIcon } from "@assets/Icons/download-solid.svg";
import { Attachment } from "../TicketDetail";


/* eslint-disable react/prop-types */ // TS > PropTypes

export const getAttLabel = (att: Attachment) =>
  att.filename || att.name || "Attachment";

export type DownloadMap = Record<string | number, boolean>;

type AttachmentChipProps = {
  att: Attachment;
  downloading?: boolean;
  onDownload: (att: Attachment) => void | Promise<void>;
  compact?: boolean;
  className?: string;
};

export const AttachmentChip = React.memo(function AttachmentChip({
  att,
  downloading = false,
  onDownload,
  compact = false,
  className = "",
}: AttachmentChipProps) {
  const label = getAttLabel(att);

  return (
    <div
      className={[
        "flex items-center justify-between",
        "bg-semantic-bg-primary rounded shadow-sm border border-gray-100",
        compact ? "px-2 py-1 gap-2" : "px-3 py-2 gap-3",
        "min-w-0", // allow truncate
        className,
      ].join(" ")}
    >
      <span className={["truncate", compact ? "text-xs" : "text-sm"].join(" ")} title={label}>
        {label}
      </span>

      <GenericButton
        variant="brand"
        size={compact ? "xsm" : "sm"}
        label={downloading ? "Downloading..." : "Download"}
        isIconOnly
        icon={<DownloadIcon className="w-3 text-white fill-white" />}
        onClick={() => onDownload(att)}
        disabled={downloading}
        aria-label={`Download ${label}`}
      />
    </div>
  );
});

type AttachmentListProps = {
  attachments: Attachment[];
  downloadingMap?: DownloadMap;
  onDownload: (att: Attachment) => void | Promise<void>;
  compact?: boolean;
  columns?: 1 | 2;      // We keep this explicit to avoid Tailwind purge issues
  oneVisibleRow?: boolean; // clamp height to one row
  className?: string;
};

export const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  downloadingMap = {},
  onDownload,
  compact = false,
  columns = 2,
  oneVisibleRow = false,
  className = "",
}) => {
  const gridCols = columns === 1 ? "grid-cols-1" : "grid-cols-2";
  const rowHeightPx = compact ? 50 : 70;

  return (
    <div
      className={[
        "grid gap-2 p-2 border border-gray-200 rounded",
        gridCols,
        className,
      ].join(" ")}
      style={
        oneVisibleRow
          ? { maxHeight: rowHeightPx, overflowY: "auto", minWidth: 0 }
          : undefined
      }
    >
      {attachments.map((att) => (
        <AttachmentChip
          key={att.id}
          att={att}
          downloading={!!downloadingMap[att.id]}
          onDownload={onDownload}
          compact={compact}
        />
      ))}
    </div>
  );
};
